// apps/client/src/lib/contacts/loanContactMerge.ts
//
// Shared, browser-safe engine for the loan-keyed contact store.
//
// SINGLE definition of "what a contact is and how we dedupe", extracted from
// useLoanContacts.ts (Phase 0 of the Contact-Activity unification) so BOTH the
// per-loan hook (useLoanContacts) and the company-wide feed (useContactActivity)
// share one normalization + merge implementation. Pure functions only — no React,
// no Firestore I/O — so they are unit-testable in isolation. Firestore Timestamps
// are duck-typed (`.toDate()` / `.seconds`) rather than `instanceof`-checked, so the
// engine has zero firebase dependency (the write-path `sanitizeEntry`, which needs
// `Timestamp.now()`, stays in the hook).
//
// Convergence is phased (Expand → Migrate → Contract): the canonical store
// `loan_contacts/{loanId}` co-exists with the 4 legacy per-page collections during
// the transition; canonical wins on id collisions, fingerprint keys normalize back
// to bare loan_id, and a doc with `contacts: []` is an authoritative empty (no
// resurrection from residual flat fields).

// ---------------------------------------------------------------------------
// Public contract (consumers code against these — do not deviate)
// ---------------------------------------------------------------------------

export type ContactChannel = 'phone' | 'email' | 'sms' | 'meeting';
export type ContactOutcome = 'reached' | 'left_message' | 'no_answer' | 'emailed' | 'other';
export type ContactSourcePage =
  | 'Discharges'
  | 'Acquired Books'
  | 'Settlement Age'
  | 'Trail Drop-Off'
  | 'Arrears'
  | 'Refinance Intelligence'
  | 'Clawbacks';

export interface LoanContactEntry {
  id: string;
  channels: ContactChannel[];
  date: string | null; // 'YYYY-MM-DD'
  broker: string; // "made by"
  outcome: ContactOutcome | null;
  comments: string;
  sourcePage: ContactSourcePage;
  createdAt?: any; // Timestamp | Date | string
  createdBy?: string;
  // Server-stamped audit fields (written by the mutateLoanContact callable).
  // Optional because legacy + pre-callable entries predate them.
  createdByUid?: string;
  updatedAt?: any;
  updatedBy?: string;
  updatedByUid?: string;
  originStore?: 'loan_contacts' | 'client_contacts';
}

// icon = lucide name: phone, mail, message-square, users
export const CONTACT_CHANNELS: Record<ContactChannel, { label: string; icon: string }> = {
  phone: { label: 'Phone', icon: 'phone' },
  email: { label: 'Email', icon: 'mail' },
  sms: { label: 'SMS', icon: 'message-square' },
  meeting: { label: 'Meeting', icon: 'users' },
};

export const CONTACT_OUTCOMES: Record<ContactOutcome, string> = {
  reached: 'Reached',
  left_message: 'Left message',
  no_answer: 'No answer',
  emailed: 'Emailed',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// Collections + legacy shapes (dual-read during transition)
// ---------------------------------------------------------------------------

export const CANONICAL_COLLECTION = 'loan_contacts';

// NOTE: the per-page legacy collections (trail_dropoff_contacts, arrears_contacts,
// settlement_contacts, acquired_book_contacts) were RETIRED 2026-06-30 (backfilled
// into loan_contacts, reconciled, data deleted, reads + writes denied in rules), so
// the LEGACY_COLLECTIONS subscription registry is gone. The legacy SHAPE helpers
// below (LegacyContactDoc / flattenLegacyDoc / mapLegacyEntry) are retained because
// useContactActivity reuses them to read the Client Care `client_contacts` mirror.

// Legacy per-page entry shape (email/phone booleans + separate dates).
export interface LegacyContactEntry {
  id: string;
  contactedByEmail?: boolean;
  emailDate?: string | null;
  contactedByPhone?: boolean;
  phoneDate?: string | null;
  contactBroker?: string;
  comments?: string;
  createdAt?: any;
  createdBy?: string;
}

export interface LegacyContactDoc {
  settlementKey: string; // = loan_id
  customerName?: string;
  broker?: string;
  contacts?: LegacyContactEntry[];
  // Old flat format (SettlementAge backward compat)
  contactedByEmail?: boolean;
  emailDate?: string | null;
  contactedByPhone?: boolean;
  phoneDate?: string | null;
  contactBroker?: string;
  comments?: string;
  lastUpdated?: any;
  updatedBy?: string;
}

// A tombstone records a deleted entry id so a backfilled legacy copy cannot
// resurrect it during dual-read. Written by the mutateLoanContact callable.
export interface ContactTombstone {
  id: string;
  deletedByUid?: string;
  deletedByName?: string;
  deletedAt?: any;
}

// Canonical doc shape stored at loan_contacts/{loanId}.
export interface CanonicalContactDoc {
  loanId: string;
  customerName?: string;
  broker?: string;
  contacts?: LoanContactEntry[];
  tombstones?: ContactTombstone[];
  lastUpdated?: any;
  updatedBy?: string;
}

// Collect the deleted entry ids from a canonical doc's tombstones.
export function extractTombstoneIds(data: CanonicalContactDoc): string[] {
  return Array.isArray(data.tombstones)
    ? data.tombstones.map((t) => t.id).filter((id): id is string => !!id)
    : [];
}

// ---------------------------------------------------------------------------
// Helpers (pure)
// ---------------------------------------------------------------------------

// Strip a trailing fingerprint suffix (`-YYYYMMDD`, `-NA`, `-unknown`) from a
// settlementKey to recover the bare loan_id. Applied ONLY to fingerprinted
// collections so a loan_id that legitimately ends in 8 digits is never mangled.
export function stripFingerprintSuffix(settlementKey: string): string {
  return settlementKey.replace(/-(?:\d{8}|NA|unknown)$/, '');
}

export function toYMD(val: any): string | null {
  if (!val) return null;
  let d: Date | null = null;
  if (typeof val?.toDate === 'function') d = val.toDate();
  else if (typeof val?.seconds === 'number') d = new Date(val.seconds * 1000);
  else if (val instanceof Date) d = val;
  else if (typeof val === 'string') {
    // Already a YYYY-MM-DD string?
    if (/^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
    const parsed = new Date(val);
    d = isNaN(parsed.getTime()) ? null : parsed;
  }
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function sortableTime(entry: LoanContactEntry): number {
  // Newest-first ordering: prefer the entry date, fall back to createdAt.
  if (entry.date) {
    const t = new Date(entry.date).getTime();
    if (!isNaN(t)) return t;
  }
  const c = entry.createdAt;
  if (typeof c?.toDate === 'function') return c.toDate().getTime();
  if (typeof c?.seconds === 'number') return c.seconds * 1000;
  if (c instanceof Date) return c.getTime();
  if (typeof c === 'string') {
    const t = new Date(c).getTime();
    if (!isNaN(t)) return t;
  }
  return 0;
}

// Map a single legacy entry → canonical LoanContactEntry.
export function mapLegacyEntry(
  entry: LegacyContactEntry,
  sourcePage: ContactSourcePage
): LoanContactEntry {
  const channels: ContactChannel[] = ([] as ContactChannel[]).concat(
    entry.contactedByEmail ? (['email'] as ContactChannel[]) : [],
    entry.contactedByPhone ? (['phone'] as ContactChannel[]) : []
  );
  return {
    id: entry.id,
    channels,
    date: entry.emailDate || entry.phoneDate || toYMD(entry.createdAt),
    broker: entry.contactBroker || '',
    outcome: null,
    comments: entry.comments || '',
    sourcePage,
    createdAt: entry.createdAt,
    createdBy: entry.createdBy || '',
  };
}

// Flatten a legacy doc (array format or old flat single-entry format) → entries.
export function flattenLegacyDoc(
  docData: LegacyContactDoc,
  sourcePage: ContactSourcePage
): LoanContactEntry[] {
  // The PRESENCE of a `contacts` array is authoritative — even when empty.
  // A doc with `contacts: []` means every entry was deleted; falling through to
  // the flat-format branch would resurrect a deleted contact from residual
  // top-level fields. Mirror the legacy reader: array present → use it (may be []).
  if (Array.isArray(docData.contacts)) {
    return docData.contacts.map((e) => mapLegacyEntry(e, sourcePage));
  }
  // Old flat format (SettlementAge backward compat) — only when `contacts` is
  // absent (undefined). Synthesize one entry.
  if (docData.contactedByEmail !== undefined || docData.contactedByPhone !== undefined) {
    return [
      mapLegacyEntry(
        {
          id: `legacy-${docData.settlementKey}`,
          contactedByEmail: docData.contactedByEmail || false,
          emailDate: docData.emailDate || null,
          contactedByPhone: docData.contactedByPhone || false,
          phoneDate: docData.phoneDate || null,
          contactBroker: docData.contactBroker || '',
          comments: docData.comments || '',
          createdAt: docData.lastUpdated || null,
          createdBy: docData.updatedBy || 'System (migrated)',
        },
        sourcePage
      ),
    ];
  }
  return [];
}

// Normalize a canonical doc's entries: default a missing sourcePage to
// 'Discharges' (the canonical store's original owner). Returns [] for a doc with
// no `contacts` array (authoritative empty).
export function normalizeCanonicalEntries(data: CanonicalContactDoc): LoanContactEntry[] {
  return Array.isArray(data.contacts)
    ? data.contacts.map((c) => ({ ...c, sourcePage: c.sourcePage || 'Discharges' }))
    : [];
}

// ---------------------------------------------------------------------------
// Edit/delete authorization (UI control visibility — MUST mirror the server's
// canMutateEntry in services/client-functions/src/contacts/loanContactSchema.ts;
// the server is authoritative, this only hides controls the user can't use)
// ---------------------------------------------------------------------------

const ADMIN_CORRECT_ANY_ROLES: ReadonlySet<string> = new Set([
  'super_admin',
  'administrator',
  'office_admin',
]);

// True if `uid` (with `role`) may edit/delete this entry. Admins correct any;
// others only their own; legacy (no createdByUid) requires an admin; Client Care
// mirror entries are read-only here.
export function canEditEntry(
  role: string | undefined,
  uid: string | undefined,
  entry: { createdByUid?: string; originStore?: string }
): boolean {
  if (entry.originStore === 'client_contacts') return false;
  if (role && ADMIN_CORRECT_ANY_ROLES.has(role)) return true;
  if (!entry.createdByUid) return false;
  return !!uid && entry.createdByUid === uid;
}

// ---------------------------------------------------------------------------
// Per-loan merge (canonical wins, dedupe by id, newest-first)
// ---------------------------------------------------------------------------

// Merge the canonical-by-loan map with the legacy-by-source maps into a single
// loanId → entries[] map. Canonical entries are pushed first so they win on id
// collisions; remaining legacy entries with unseen ids are appended; each loan's
// list is sorted newest-first. Any entry id in `tombstonedIds` is suppressed
// (a deleted entry whose legacy copy still lingers must not resurrect).
export function mergeContactsByLoan(
  canonicalByLoan: Record<string, LoanContactEntry[]>,
  legacyBySource: Record<string, Record<string, LoanContactEntry[]>>,
  tombstonedIds?: ReadonlySet<string>
): Record<string, LoanContactEntry[]> {
  const merged: Record<string, LoanContactEntry[]> = {};
  const seen: Record<string, Set<string>> = {};

  const push = (loanId: string, entry: LoanContactEntry) => {
    if (tombstonedIds?.has(entry.id)) return; // suppress deleted entries
    if (!seen[loanId]) seen[loanId] = new Set();
    if (seen[loanId].has(entry.id)) return; // dedupe by id
    seen[loanId].add(entry.id);
    if (!merged[loanId]) merged[loanId] = [];
    merged[loanId].push(entry);
  };

  // Canonical first so it wins on id collisions.
  for (const [loanId, entries] of Object.entries(canonicalByLoan)) {
    for (const entry of entries) push(loanId, entry);
  }
  for (const sourceMap of Object.values(legacyBySource)) {
    for (const [loanId, entries] of Object.entries(sourceMap)) {
      for (const entry of entries) push(loanId, entry);
    }
  }

  // Sort newest-first within each loan.
  for (const loanId of Object.keys(merged)) {
    merged[loanId].sort((a, b) => sortableTime(b) - sortableTime(a));
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Company-wide activity feed (ContactActivityPage)
// ---------------------------------------------------------------------------

// Feed sources = the canonical per-entry sourcePages + Client Care (whose
// contacts live in the client-keyed client_contacts store).
export type FeedSourcePage = ContactSourcePage | 'Client Care';

// A single row in the company-wide Contacts Activity feed. Unlike a per-loan
// entry it carries the loan(s) it's attached to and an optional Client Care
// client id, so the same logged contact (e.g. mirrored from Client Care into a
// loan) collapses to ONE row that still deep-links to both.
export interface ContactActivityEntry {
  id: string; // entry id — globally unique, used as the stable activityKey
  channels: ContactChannel[];
  outcome: ContactOutcome | null;
  date: string | null; // contact date (YYYY-MM-DD)
  comments: string;
  broker: string; // "made by"
  createdAt: Date; // logged-at — drives date-range filtering + sort
  createdBy: string; // actor display name
  createdByUid?: string; // actor uid (audit)
  customerName: string;
  sourcePage: FeedSourcePage;
  loanIds: string[]; // loan(s) this contact is attached to (empty = client-only)
  clientId?: string; // Client Care client id (deep-link target)
}

// One contribution to the feed from a single store. The hook builds these from
// loan_contacts (canonical), the 4 legacy collections, and client_contacts.
export interface FeedContribution {
  entry: LoanContactEntry;
  feedSourcePage: FeedSourcePage;
  canonical: boolean; // canonical (loan_contacts) wins presentation on id collision
  customerName: string;
  loanId?: string; // loan-keyed stores
  clientId?: string; // client_contacts
}

export function toJsDate(val: any): Date {
  if (!val) return new Date(0);
  if (typeof val?.toDate === 'function') return val.toDate();
  if (typeof val?.seconds === 'number') return new Date(val.seconds * 1000);
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    const d = new Date(val);
    return isNaN(d.getTime()) ? new Date(0) : d;
  }
  return new Date(0);
}

// Aggregate all contributions into the company feed: dedupe globally by entry id
// (canonical wins presentation regardless of order), UNION the loan/client
// references so a mirrored Client Care contact keeps both its loanId and its
// clientId, drop tombstoned ids, and sort newest-first by logged-at.
export function aggregateContactFeed(
  contributions: FeedContribution[],
  tombstonedIds?: ReadonlySet<string>
): ContactActivityEntry[] {
  const byId = new Map<string, ContactActivityEntry & { _canonical: boolean }>();

  const present = (c: FeedContribution) => ({
    channels: Array.isArray(c.entry.channels) ? c.entry.channels : [],
    outcome: c.entry.outcome ?? null,
    date: c.entry.date ?? null,
    comments: c.entry.comments ?? '',
    broker: c.entry.broker ?? '',
    createdAt: toJsDate(c.entry.createdAt),
    createdBy: c.entry.createdBy ?? '',
    createdByUid: c.entry.createdByUid,
    sourcePage: c.feedSourcePage,
  });

  for (const c of contributions) {
    const id = c.entry.id;
    if (tombstonedIds?.has(id)) continue;
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, {
        id,
        ...present(c),
        customerName: c.customerName || 'Unknown Customer',
        loanIds: c.loanId ? [c.loanId] : [],
        clientId: c.clientId,
        _canonical: c.canonical,
      });
      continue;
    }
    // Always union references, whichever store won presentation.
    if (c.loanId && !existing.loanIds.includes(c.loanId)) existing.loanIds.push(c.loanId);
    if (c.clientId && !existing.clientId) existing.clientId = c.clientId;
    // Canonical wins presentation (order-independent).
    if (c.canonical && !existing._canonical) {
      Object.assign(existing, present(c), { _canonical: true });
      if (c.customerName) existing.customerName = c.customerName;
    }
  }

  return Array.from(byId.values())
    .map(({ _canonical, ...e }) => e)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
